import { useState, useEffect, useCallback } from 'react';

export type AvatarQuality = 'premium' | 'standard' | 'minimal';
export type AvatarEmotion = 'neutral' | 'happy' | 'thinking' | 'surprised' | 'concerned';

export interface AvatarPreferences {
  quality: AvatarQuality;
  emotionsEnabled: boolean;
  particleCount: 'high' | 'medium' | 'low';
  glowEffects: boolean;
}

const DEFAULT_PREFERENCES: AvatarPreferences = {
  quality: 'premium',
  emotionsEnabled: true,
  particleCount: 'high',
  glowEffects: true,
};

const STORAGE_KEY = 'geniebot-avatar-preferences';

// Detect if device is low-end based on hardware concurrency and memory
const detectLowEndDevice = (): boolean => {
  const cores = navigator.hardwareConcurrency || 2;
  const memory = (navigator as any).deviceMemory || 4;
  return cores <= 2 || memory <= 2;
};

export const useAvatarPreferences = () => {
  const [preferences, setPreferences] = useState<AvatarPreferences>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
    
    // Auto-detect and set lower quality for low-end devices
    if (detectLowEndDevice()) {
      return {
        ...DEFAULT_PREFERENCES,
        quality: 'standard' as AvatarQuality,
        particleCount: 'low' as const,
        glowEffects: false,
      };
    }
    
    return DEFAULT_PREFERENCES;
  });

  const [currentEmotion, setCurrentEmotion] = useState<AvatarEmotion>('neutral');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const updatePreferences = useCallback((updates: Partial<AvatarPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  }, []);

  const setQuality = useCallback((quality: AvatarQuality) => {
    updatePreferences({ quality });
  }, [updatePreferences]);

  const setEmotion = useCallback((emotion: AvatarEmotion) => {
    if (preferences.emotionsEnabled) {
      setCurrentEmotion(emotion);
    }
  }, [preferences.emotionsEnabled]);

  const resetToDefaults = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  // Get particle count number based on setting
  const getParticleCount = useCallback(() => {
    switch (preferences.particleCount) {
      case 'high': return 2000;
      case 'medium': return 1000;
      case 'low': return 500;
      default: return 2000;
    }
  }, [preferences.particleCount]);

  // Check if should use WebGL (premium quality)
  const usePremiumFallback = preferences.quality === 'premium';

  return {
    preferences,
    updatePreferences,
    setQuality,
    setEmotion,
    currentEmotion,
    resetToDefaults,
    getParticleCount,
    usePremiumFallback,
    isLowEndDevice: detectLowEndDevice(),
  };
};

// Comprehensive emotion detection helper with expanded vocabulary
export const detectEmotionFromText = (text: string): AvatarEmotion => {
  const lowerText = text.toLowerCase();
  
  // Happy indicators - expanded vocabulary
  const happyWords = [
    // Basic positive
    'great', 'awesome', 'wonderful', 'excellent', 'happy', 'love', 'thanks', 'thank you', 'perfect', 'amazing',
    // Enthusiasm
    'fantastic', 'brilliant', 'superb', 'outstanding', 'incredible', 'magnificent', 'marvelous', 'fabulous',
    // Success/achievement
    'congratulations', 'congrats', 'well done', 'good job', 'nice work', 'bravo', 'hooray', 'yay', 'woohoo',
    // Joy/excitement
    'excited', 'thrilled', 'delighted', 'overjoyed', 'elated', 'ecstatic', 'cheerful', 'joyful', 'blissful',
    // Gratitude
    'grateful', 'appreciate', 'thankful', 'blessed', 'lucky', 'fortunate',
    // Affirmation
    'absolutely', 'definitely', 'certainly', 'of course', 'sure thing', 'no problem', 'glad to help',
    // Fun
    'fun', 'enjoy', 'pleasure', 'delight', 'laugh', 'smile', 'grin', 'cheer',
    // Emojis
    '😊', '😃', '😄', '😁', '🎉', '🎊', '👍', '👏', '❤️', '💖', '✨', '🌟', '😍', '🥳', '🙌', '💯',
    '!', '!!', '!!!'
  ];
  if (happyWords.some(word => lowerText.includes(word))) {
    return 'happy';
  }
  
  // Thinking indicators - expanded vocabulary
  const thinkingWords = [
    // Contemplation
    'hmm', 'hmmm', 'let me think', 'considering', 'analyzing', 'processing', 'interesting', 'wondering',
    // Uncertainty/exploration
    'perhaps', 'maybe', 'might', 'could be', 'possibly', 'probably', 'likely', 'presumably',
    // Analysis
    'evaluating', 'assessing', 'examining', 'reviewing', 'pondering', 'contemplating', 'reflecting',
    // Research/investigation
    'researching', 'investigating', 'exploring', 'looking into', 'checking', 'searching', 'finding',
    // Deliberation
    'weighing options', 'on one hand', 'on the other hand', 'trade-off', 'pros and cons',
    // Cognitive process
    'let me see', 'let me check', 'give me a moment', 'one moment', 'working on it', 'figuring out',
    // Questions/curiosity
    'curious', 'intriguing', 'fascinating', 'thought-provoking', 'complex', 'nuanced',
    // Indicators
    '...', '🤔', '💭', '🧠', '📊', '🔍'
  ];
  if (thinkingWords.some(word => lowerText.includes(word))) {
    return 'thinking';
  }
  
  // Surprised indicators - expanded vocabulary
  const surprisedWords = [
    // Exclamation
    'wow', 'whoa', 'oh!', 'really?', 'no way', 'incredible', 'unbelievable', 'holy',
    // Astonishment
    'astonishing', 'astounding', 'stunning', 'shocking', 'mind-blowing', 'jaw-dropping', 'breathtaking',
    // Disbelief
    'seriously?', 'are you kidding', 'you\'re kidding', 'can\'t believe', "didn't expect", 'unexpected',
    // Amazement
    'remarkable', 'extraordinary', 'phenomenal', 'sensational', 'spectacular', 'tremendous',
    // Discovery
    'discovered', 'found out', 'realized', 'turns out', 'it appears', 'surprisingly',
    // Revelation
    'revelation', 'breakthrough', 'game-changer', 'plot twist', 'twist', 'suddenly',
    // Intensity
    'insane', 'crazy', 'wild', 'bonkers', 'nuts', 'ridiculous', 'absurd',
    // Emojis
    '!?', '?!', '😮', '😲', '🤯', '😱', '😳', '🙀', '⁉️', '❗', '‼️', '💥', '🔥'
  ];
  if (surprisedWords.some(word => lowerText.includes(word))) {
    return 'surprised';
  }
  
  // Concerned indicators - expanded vocabulary
  const concernedWords = [
    // Apology/regret
    'sorry', 'apologize', 'apologies', 'regret', 'unfortunately', 'sadly', 'regrettably',
    // Problems
    'issue', 'problem', 'error', 'failed', 'cannot', "can't", 'unable', 'impossible', 'difficult',
    // Worry
    'worried', 'concern', 'concerned', 'anxious', 'nervous', 'uneasy', 'troubled', 'disturbed',
    // Caution
    'warning', 'caution', 'careful', 'be aware', 'watch out', 'heads up', 'alert',
    // Negative outcomes
    'mistake', 'wrong', 'incorrect', 'invalid', 'broken', 'crashed', 'down', 'unavailable',
    // Sympathy
    'understand your frustration', 'i see the issue', 'that\'s frustrating', 'i hear you',
    // Limitation
    "don't have", "don't know", 'not sure', 'uncertain', 'unclear', 'limited', 'restriction',
    // Risk
    'risk', 'danger', 'threat', 'vulnerable', 'security', 'privacy', 'sensitive',
    // Disappointment
    'disappointed', 'letdown', 'unfortunate', 'setback', 'obstacle', 'challenge', 'hurdle',
    // Emojis
    '😟', '😢', '😰', '😥', '🥺', '😔', '💔', '⚠️', '🚨', '❌', '🛑'
  ];
  if (concernedWords.some(word => lowerText.includes(word))) {
    return 'concerned';
  }
  
  return 'neutral';
};
